import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { ItemStatus } from "@common/ADAPT.Common.Model/organisation/item-status";
import { PersonFlag } from "@common/ADAPT.Common.Model/person/person-flag.enum";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { ActivityPageService } from "@org-common/lib/activities/activity-page.service";
import { CulturalActivityMetadataByType, NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { debounceTime } from "rxjs/operators";
import { PersonFlagService } from "../../person/person-flag.service";
import { IActivityGroup } from "../activities-metadata";

@Component({
    selector: "adapt-personal-work-activity-section",
    templateUrl: "./personal-work-activity-section.component.html",
    styleUrls: ["./personal-work-activity-section.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class PersonalWorkActivitySectionComponent extends BaseComponent implements OnChanges {
    public readonly section = CulturalActivityMetadataByType[NonCulturalActivityType.Actions];

    @Input({ required: true }) public activityGroup!: IActivityGroup<Item>;
    @Input() public blurPrivateActions = true;
    @Input() public countTooltip?: string;
    @Input() public isLoading = false;
    @Input() public error?: any;
    @Input() public fallbackText?: string;
    @Output() public blurPrivateActionsChange = new EventEmitter<boolean>();

    public blurPrivateActionsDefault = false;

    public itemMenu?: IAdaptMenuItem[];

    public todoItems: Item[] = [];
    public inProgressItems: Item[] = [];

    public constructor(
        private activitiesService: ActivitiesService,
        private personFlagService: PersonFlagService,
        activityPageService: ActivityPageService,
    ) {
        super();

        this.personFlagService.isFlagEnabled$(PersonFlag.BlurPrivateActions).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((blurPrivateActions) => {
            this.blurPrivateActionsDefault = blurPrivateActions;
            this.buildMenu();
        });

        activityPageService.filterUpdated$.pipe(
            debounceTime(100),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.ngOnChanges());
    }

    public ngOnChanges() {
        this.todoItems = this.activityGroup.allItems
            .filter((item) => item.status === ItemStatus.ToDo)
            .filter((item) => item.extensions.isOverdue
                ? this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Overdue)
                : this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Upcoming))
            .slice(0, 3);

        this.inProgressItems = this.activityGroup.allItems
            .filter((item) => item.status === ItemStatus.InProgress)
            .filter((item) => item.extensions.isOverdue
                ? this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Overdue)
                : this.activitiesService.isActivityStatusVisible(CulturalActivityStatus.Upcoming))
            .slice(0, 3);
    }

    public toggleBlurPrivateActions(clickEvent: Event) {
        this.blurPrivateActions = !this.blurPrivateActions;
        this.blurPrivateActionsChange.emit(this.blurPrivateActions);
        if (this.blurPrivateActions && !this.blurPrivateActionsDefault) {
            // if blur -> save.
            // The reason a person would be blurring private actions is because he expected them to be blurred next time.
            // Otherwise, what's the point of me blurring my own actions. If blurred, and next time I logged in and not blurred, they will be leaked.
            // Discussed with Steve, and here is the middle ground.
            this.toggleBlurPrivateActionDefault().subscribe();
        }

        // don't want this click to toggle the collapse
        clickEvent.stopPropagation();
    }

    @Autobind
    public toggleBlurPrivateActionDefault() {
        this.blurPrivateActionsDefault = !this.blurPrivateActionsDefault;
        return this.personFlagService.setFlagAndSave(PersonFlag.BlurPrivateActions, this.blurPrivateActionsDefault);
    }

    private buildMenu() {
        this.itemMenu = [{
            icon: MenuComponent.SmallRootMenu.icon,
            items: [{
                text: "Blur private items by default",
                isChecked: this.blurPrivateActionsDefault,
                onIsCheckedChange: () => {
                    this.toggleBlurPrivateActionDefault().subscribe();
                },
            }],
        }];
    }
}
